import { Modal, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  errorMessage,
  type FeedbackResponse,
  listAllFeedbackQueryKey,
  schemas,
  updateFeedbackStatusMutation,
} from "@/api";
import { useAppForm } from "@/components/form/form";
import { FEEDBACK_STATUSES } from "./feedbackLabels";
import { feedbackItemQueryKey } from "./useFeedbackItem";

// Rules come from the generated FeedbackStatusUpdate schema (status pattern, notes length).
const update = schemas.zFeedbackStatusUpdate.shape;
const updateSchema = z.object({ status: update.status, adminNotes: update.adminNotes.unwrap().unwrap() });
type UpdateValues = z.infer<typeof updateSchema>;

/** Small modal form: change status and admin notes, PATCH, toast. */
export function UpdateStatusModal({
  item,
  open,
  onClose,
}: Readonly<{ item: FeedbackResponse; open: boolean; onClose: () => void }>) {
  const { t } = useTranslation("adminFeedback");
  const toast = useToast();
  const queryClient = useQueryClient();
  const patch = useMutation(updateFeedbackStatusMutation());

  const form = useAppForm({
    defaultValues: { status: item.status, adminNotes: item.adminNotes ?? "" } as UpdateValues,
    validators: { onChange: updateSchema },
    onSubmit: async ({ value }) => {
      try {
        const res = await patch.mutateAsync({
          path: { feedback_uuid: item.uuid },
          body: { status: value.status, adminNotes: value.adminNotes.trim() || null },
          throwOnError: true,
        });
        queryClient.setQueryData(feedbackItemQueryKey(item.uuid), res.data);
        await queryClient.invalidateQueries({
          queryKey: listAllFeedbackQueryKey(),
          predicate: (q) => q.queryKey.length === 1,
        });
        toast.success(t("update.saved"));
        onClose();
      } catch (error) {
        toast.error(errorMessage(error, t("update.failed")));
      }
    },
  });

  const close = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={close} title={t("update.title")}>
      <form
        aria-label={t("update.label")}
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppField name="status">
          {(field) => (
            <field.SelectField
              label={t("update.status")}
              options={FEEDBACK_STATUSES.map((s) => ({ value: s, label: t(`statuses.${s}`) }))}
            />
          )}
        </form.AppField>
        <form.AppField name="adminNotes">
          {(field) => <field.TextareaField label={t("update.notes")} placeholder={t("update.notesPlaceholder")} />}
        </form.AppField>
        <form.AppForm>
          <form.SubmitActions onCancel={close} submitLabel={t("update.save")} />
        </form.AppForm>
      </form>
    </Modal>
  );
}
